/**
 * Right-side properties panel for the application header.
 *
 * Same shape and behaviour as the Symbology and Label Properties panels: it
 * slides over the map so the header stays visible while it is being designed,
 * and every change is applied live rather than behind an Apply button — the
 * header is small enough that the preview IS the confirmation.
 */

import { X } from 'lucide-react';
import type { AppColorTheme, HeaderTheme } from '../../types';
import {
  ColorField,
  PropertySection,
  SliderField,
  ToggleField,
} from '../layers/symbology/SymbolProperties';
import ImagePicker from '../settings/ImagePicker';
import { HEADER_DEFAULTS, HEADER_FONTS, resolveHeaderStyle } from './AppHeader';

/** Segmented control, matching the one the symbology panel uses. */
function Segmented<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { id: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="py-1.5">
      <span className="text-xs text-slate-600">{label}</span>
      <div
        className="mt-1 grid gap-1 rounded-lg bg-slate-100 p-1"
        style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}
      >
        {options.map((option) => (
          <button
            key={String(option.id)}
            type="button"
            aria-pressed={value === option.id}
            onClick={() => onChange(option.id)}
            className={`truncate rounded-md px-1.5 py-1 text-[11px] font-medium transition ${
              value === option.id
                ? 'bg-white text-blue-700 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function HeaderProperties({
  open,
  theme,
  onChange,
  colorTheme,
  onColorThemeChange,
  onClose,
}: {
  open: boolean;
  theme: HeaderTheme;
  onChange: (next: HeaderTheme) => void;
  /** App-wide theme; see AppModule.colorTheme. */
  colorTheme: AppColorTheme | undefined;
  onColorThemeChange: (next: AppColorTheme | undefined) => void;
  onClose: () => void;
}) {
  const resolved = resolveHeaderStyle(theme);
  const set = (patch: Partial<HeaderTheme>) => onChange({ ...theme, ...patch });
  const setColorTheme = (patch: Partial<AppColorTheme>) =>
    onColorThemeChange({ base: colorTheme?.base ?? 'light', ...colorTheme, ...patch });

  const fontId =
    HEADER_FONTS.find((font) => font.stack === (theme.fontFamily ?? ''))?.id ?? 'system';

  return (
    <aside
      role="dialog"
      aria-label="Header design"
      aria-hidden={!open}
      className={`gl-card gl-surface--strong gl-surface--over-map absolute right-2 top-2 z-[4000] flex w-[300px] flex-col overflow-hidden transition-transform duration-300 ease-out ${
        open ? 'translate-x-0' : 'pointer-events-none invisible translate-x-[calc(100%+1rem)]'
      }`}
      style={{
        height: 'calc(100% - 1rem)',
        borderRadius: 'var(--glass-radius-lg)',
        background: 'var(--glass-bg-strong)',
        // Only filter while shown — a backdrop-filter on a hidden, always-mounted
        // panel still blurs whatever is behind it.
        backdropFilter: open ? 'blur(var(--glass-blur)) saturate(var(--glass-saturate))' : undefined,
        WebkitBackdropFilter: open
          ? 'blur(var(--glass-blur)) saturate(var(--glass-saturate))'
          : undefined,
        border: '1px solid var(--glass-border)',
        boxShadow: 'var(--glass-shadow-lg)',
      }}
    >
      <header className="gl-header flex flex-shrink-0 items-start justify-between px-4 py-2.5">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[#0F2942]">Header</h3>
          <p className="text-[11px] text-slate-500">Colour, type and layout</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close header design"
          className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <PropertySection title="App theme">
          <p className="pb-1.5 text-[10px] leading-snug text-slate-400">
            Recolours the whole application — TOC, tools, popups and the
            attribute table — not just this header bar.
          </p>

          <Segmented
            label="Base"
            value={colorTheme?.base ?? 'light'}
            options={[
              { id: 'light' as const, label: 'Light' },
              { id: 'dark' as const, label: 'Dark' },
            ]}
            onChange={(base) => setColorTheme({ base })}
          />

          <ColorField
            label="Primary"
            value={colorTheme?.primaryColor ?? '#2563EB'}
            onChange={(primaryColor) => setColorTheme({ primaryColor })}
          />
          <ColorField
            label="Accent"
            value={colorTheme?.accentColor ?? '#0D9488'}
            onChange={(accentColor) => setColorTheme({ accentColor })}
          />

          {colorTheme && (
            <button
              type="button"
              onClick={() => onColorThemeChange(undefined)}
              className="gl-btn gl-btn--secondary mt-2 w-full py-1.5 text-[11px]"
            >
              Reset to default
            </button>
          )}
        </PropertySection>

        <PropertySection title="Text">
          <label className="block py-1.5">
            <span className="text-xs text-slate-600">Subtitle</span>
            <input
              value={theme.subtitle ?? ''}
              onChange={(event) => set({ subtitle: event.target.value })}
              placeholder="e.g. Distribution Network"
              className="gl-input mt-1 w-full px-2.5 py-1.5 text-xs"
            />
          </label>

          <Segmented
            label="Subtitle position"
            value={resolved.placement}
            options={[
              { id: 'inline' as const, label: 'Beside title' },
              { id: 'below' as const, label: 'Below title' },
            ]}
            onChange={(subtitlePlacement) => set({ subtitlePlacement })}
          />

          <Segmented
            label="Alignment"
            value={resolved.align}
            options={[
              { id: 'left' as const, label: 'Left' },
              { id: 'center' as const, label: 'Center' },
              { id: 'right' as const, label: 'Right' },
            ]}
            onChange={(titleAlign) => set({ titleAlign })}
          />
        </PropertySection>

        <PropertySection title="Typography">
          <Segmented
            label="Font"
            value={fontId}
            options={HEADER_FONTS.map((font) => ({ id: font.id, label: font.label }))}
            onChange={(id) =>
              set({ fontFamily: HEADER_FONTS.find((font) => font.id === id)?.stack || undefined })
            }
          />

          <SliderField
            label="Title size"
            value={resolved.titleSize}
            min={11}
            max={34}
            suffix=" px"
            onChange={(titleSize) => set({ titleSize })}
          />

          <Segmented
            label="Title weight"
            value={resolved.titleWeight}
            options={[
              { id: 400, label: 'Regular' },
              { id: 500, label: 'Medium' },
              { id: 600, label: 'Semibold' },
              { id: 700, label: 'Bold' },
            ]}
            onChange={(titleWeight) => set({ titleWeight: titleWeight as 400 | 500 | 600 | 700 })}
          />

          <ToggleField
            label="Italic title"
            checked={resolved.titleItalic}
            onChange={(titleItalic) => set({ titleItalic })}
          />

          <SliderField
            label="Subtitle size"
            value={resolved.subtitleSize}
            min={9}
            max={22}
            suffix=" px"
            onChange={(subtitleSize) => set({ subtitleSize })}
          />
        </PropertySection>

        <PropertySection title="Colour">
          <ColorField
            label="Background"
            value={theme.background ?? '#FFFFFF'}
            onChange={(background) => set({ background })}
          />
          <ColorField
            label="Title"
            value={theme.textColor ?? '#0F2942'}
            onChange={(textColor) => set({ textColor })}
          />
          <ColorField
            label="Subtitle"
            value={theme.subtitleColor ?? '#64748B'}
            onChange={(subtitleColor) => set({ subtitleColor })}
          />
          <p className="pt-1 text-[10px] leading-snug text-slate-400">
            Reset the colours to let the header follow the app theme — the text then
            switches to white by itself on a dark background.
          </p>
          <button
            type="button"
            onClick={() =>
              set({ background: undefined, textColor: undefined, subtitleColor: undefined })
            }
            className="gl-btn gl-btn--secondary mt-2 w-full py-1.5 text-[11px]"
          >
            Reset colours
          </button>
        </PropertySection>

        <PropertySection title="Logo">
          <ToggleField
            label="Show logo"
            description="The mark to the left of the title."
            checked={resolved.showLogo}
            onChange={(showLogo) => set({ showLogo })}
          />

          {/* Only offered once there is a logo to design. Leaving the picker
              visible under a switched-off toggle invites someone to upload an
              image and then wonder why the header never changed. */}
          {resolved.showLogo && (
            <div className="space-y-3 pt-2">
              <ImagePicker
                label="Logo image"
                value={theme.logoUrl}
                onChange={(logoUrl) => set({ logoUrl })}
                hint="Leave empty for the built-in compass mark. A PNG or SVG with a transparent background sits best on a coloured header."
                /* 512px is ample for a mark drawn at most 48px tall, even on a
                   high-density screen. The stored image is squeezed harder
                   than the home page's, because this one rides inside the
                   module document that every editor loads and saves. */
                maxDimension={512}
                targetBytes={120 * 1024}
              />

              <SliderField
                label="Logo height"
                value={resolved.logoSize}
                min={18}
                max={48}
                suffix=" px"
                onChange={(logoSize) => set({ logoSize })}
              />
            </div>
          )}
        </PropertySection>

        <PropertySection title="Reset">
          <p className="text-[10px] leading-snug text-slate-400">
            Puts the type, colours and layout back to their defaults. The
            subtitle and logo are kept — they are this application&rsquo;s
            content, not its styling.
          </p>
          <button
            type="button"
            onClick={() =>
              onChange({
                subtitle: theme.subtitle,
                subtitlePlacement: HEADER_DEFAULTS.subtitlePlacement,
                logoUrl: theme.logoUrl,
              })
            }
            className="gl-btn gl-btn--secondary mt-2 w-full py-1.5 text-[11px]"
          >
            Reset header to defaults
          </button>
        </PropertySection>
      </div>
    </aside>
  );
}
